import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { deepSearch, type PagefindHit } from '../lib/pagefind'
import { slugFromPageUrl, useDocsIndex } from '../lib/docsIndex'
import { useDebounced } from '../lib/useAsync'
import { docPath, type DocSummary, type Facet } from '../lib/types'
import { Chip, Spinner, VisibilityBadge, cx } from './ui'

interface Row {
  key: string
  to: string
  title: string
  /** Plain text for API rows; sanitized <mark> HTML for Pagefind rows. */
  subtitle: string
  excerptHtml?: string
  visibility: 'public' | 'internal'
  group: 'Docs' | 'Page content'
}

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { pathBySlug } = useDocsIndex()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [tag, setTag] = useState<string | null>(null)
  const [docs, setDocs] = useState<DocSummary[]>([])
  const [facets, setFacets] = useState<Facet[]>([])
  const [deep, setDeep] = useState<PagefindHit[]>([])
  const [busy, setBusy] = useState(false)
  const [cursor, setCursor] = useState(0)

  const debounced = useDebounced(query, 180)

  useEffect(() => {
    if (!open) return
    setCursor(0)

    const ac = new AbortController()
    setBusy(true)

    // Primary search is authoritative and role-filtered; Pagefind is additive and
    // may be missing entirely (never published, or running `vite dev`).
    Promise.all([
      api.search({ q: debounced || undefined, tag: tag ?? undefined }, ac.signal),
      debounced.trim() ? deepSearch(debounced, 5) : Promise.resolve([]),
    ])
      .then(([primary, hits]) => {
        if (ac.signal.aborted) return
        setDocs(primary.results)
        setFacets(primary.facets)
        setDeep(hits ?? [])
      })
      .catch(() => {
        if (!ac.signal.aborted) {
          setDocs([])
          setFacets([])
          setDeep([])
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setBusy(false)
      })

    return () => ac.abort()
  }, [open, debounced, tag])

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else {
      setQuery('')
      setTag(null)
    }
  }, [open])

  const rows = useMemo<Row[]>(() => {
    const primary: Row[] = docs.map((d) => ({
      key: `doc-${d.id}`,
      to: docPath(d),
      title: d.title,
      subtitle: `${d.service} / ${d.topic}`,
      visibility: d.visibility,
      group: 'Docs',
    }))

    const seenPaths = new Set(docs.map((d) => docPath(d)))
    const secondary: Row[] = deep.flatMap((h) => {
      const slug = slugFromPageUrl(h.url)
      const path = slug ? pathBySlug.get(slug) : undefined
      // A hit we cannot map to a doc has no in-app destination — drop it rather
      // than sending the reader to a bare HTML file.
      if (!path || seenPaths.has(path)) return []
      return [
        {
          key: `pf-${path}`,
          to: path,
          title: h.title,
          subtitle: '',
          excerptHtml: h.excerpt,
          visibility: 'public' as const,
          group: 'Page content' as const,
        },
      ]
    })

    return [...primary, ...secondary]
  }, [docs, deep, pathBySlug])

  const go = useCallback(
    (row: Row) => {
      navigate(row.to)
      onClose()
    },
    [navigate, onClose],
  )

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return onClose()
    if (!rows.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => (c + 1) % rows.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => (c - 1 + rows.length) % rows.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[cursor]
      if (row) go(row)
    }
  }

  if (!open) return null

  let lastGroup = ''

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 px-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search documentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <SearchIcon />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search docs, tags, page content…"
            className="h-14 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-subtle"
          />
          {busy && <Spinner label="" />}
          <kbd className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] text-subtle">
            ESC
          </kbd>
        </div>

        {facets.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b border-line px-4 py-2.5">
            {facets.slice(0, 12).map((f) => (
              <Chip
                key={f.tag}
                label={f.tag}
                count={f.count}
                active={tag === f.tag}
                onClick={() => setTag(tag === f.tag ? null : f.tag)}
              />
            ))}
          </div>
        )}

        <ul className="scroll-slim max-h-[52vh] overflow-y-auto p-2">
          {rows.length === 0 && !busy && (
            <li className="px-3 py-10 text-center text-sm text-subtle">
              {query ? `Nothing matches “${query}”.` : 'Start typing to search.'}
            </li>
          )}

          {rows.map((row, i) => {
            const header = row.group !== lastGroup ? row.group : null
            lastGroup = row.group
            return (
              <li key={row.key}>
                {header && (
                  <p className="px-3 pt-3 pb-1.5 text-[11px] font-semibold tracking-wide text-subtle uppercase">
                    {header}
                  </p>
                )}
                <button
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(row)}
                  className={cx(
                    'flex w-full items-center gap-3 rounded-[var(--radius-sh)] px-3 py-2 text-left',
                    i === cursor ? 'bg-accent-soft' : 'hover:bg-sunken',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-ink">{row.title}</span>
                      <VisibilityBadge visibility={row.visibility} />
                    </span>
                    {row.excerptHtml ? (
                      <span
                        className="mt-0.5 line-clamp-1 block text-xs text-subtle [&_mark]:bg-transparent [&_mark]:font-semibold [&_mark]:text-accent-text"
                        dangerouslySetInnerHTML={{ __html: row.excerptHtml }}
                      />
                    ) : (
                      row.subtitle && (
                        <span className="mt-0.5 block font-mono text-xs text-subtle">
                          {row.subtitle}
                        </span>
                      )
                    )}
                  </div>
                  {i === cursor && <span className="text-xs text-subtle">↵</span>}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4 shrink-0 text-subtle" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="7" />
    <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
  </svg>
)
